/* global ifCl, showModalInfo */
import React, {
  useState,
  useRef,
  useEffect,
  useContext,
  useCallback,
} from "react";
import styled from "styled-components";
import ProfileContext from "common/ProfileContext";
import GeminiContext, { useGeminiClient } from "./GeminiContext";
import AutoAwesomeIcon from "@mui/icons-material/AutoAwesome";
import MinimizeIcon from "@mui/icons-material/Minimize";
import SendIcon from "@mui/icons-material/Send";
import DragIndicatorIcon from "@mui/icons-material/DragIndicator";
import VerticalAlignTopIcon from "@mui/icons-material/VerticalAlignTop";
import CloseIcon from "@mui/icons-material/Close";
import { useNavigate } from "react-router-dom";
import CheckLicenseFirst from "common/CheckLicenseFirst";
import SelectInput from "common/SelectInput";
import { CLI_COMMANDS } from "./contexts/CLI_COMMANDS";
import BqnContext from "common/BqnContext";
import { getIndexes } from "common/utils";
import { CHATBOT_ACTION } from "./CHATBOT_ACTION";
import { getActiveContext } from "./contexts/customContext";

const MAX_PROMPT_LENGTH = 1500;

const Box = styled.div`
  position: fixed;
  background: #fff;
  color: #111;
  box-shadow: 0 4px 20px rgba(0, 0, 0, 0.25);
  display: flex;
  flex-direction: column;
  overflow: hidden;
  width: ${({ width }) => width}px;
  height: ${({ height }) => height}px;
  top: ${({ top }) => top}px;
  left: ${({ left }) => left}px;
  user-select: none;
  z-index: 999;
  min-width: 300px;

  transform: ${({ open }) => (open ? "scale(1)" : "scale(0.95)")};
  opacity: ${({ open }) => (open ? 1 : 0)};
  transition: transform 0.3s ease, opacity 0.3s ease;

  &.dark {
    background: #1e1e1e;
    color: #e6e6e6;
    box-shadow: 0 8px 30px rgba(0, 0, 0, 0.6);
  }

  &.floating {
    border-radius: 8px;
  }
`;

const Header = styled.div`
  display: flex;
  justify-content: space-between;
  align-items: center;
  padding: 10px;
  background: #f2f2f2;
  cursor: grab;

  .left {
    display: flex;
    align-items: center;
    gap: 8px;
  }

  .right {
    display: flex;
    align-items: center;
    gap: 6px;
  }

  button {
    background: none;
    border: none;
    cursor: pointer;
    color: inherit;
  }

  .rotate-right {
    transform: rotate(90deg) !important;
  }

  .rotate-left {
    transform: rotate(-90deg) !important;
  }

  .dark & {
    background: #2a2a2a;
    color: #ffffff;
    border-bottom: 1px solid #3a3a3a;
  }
`;

const Content = styled.div`
  flex: 1;
  display: flex;
  flex-direction: column;
  overflow: hidden;

  .chat-messages {
    user-select: text;
    flex: 1;
    padding: 15px;
    overflow-y: auto;
    font-size: 0.9rem;

    ::-webkit-scrollbar {
      background: var(--scrollbar-color);
      width: 4px;
    }

    ::-webkit-scrollbar-thumb {
      background: var(--scrollbar-thumb-color);
    }
  }

  .message {
    margin-bottom: 10px;
  }

  .message button.command-button {
    margin: 5px 5px 5px 0px;
    padding: 4px 15px;
    border-radius: 4px;
    border: 1px solid #0078d7;
    background: transparent;
    color: #0078d7;
    font-family: "AllianceNo2-SemiBold";
    font-size: 12px;
    cursor: pointer;
    transition: all 0.2s ease;

    &:hover {
      background: #0078d7;
      color: #fff;
      box-shadow: 0 2px 4px rgba(0, 0, 0, 0.1);
    }
  }

  .user {
    text-align: right;
    color: #0078d7;
  }

  .bot {
    text-align: left;
  }

  table {
    margin: 15px 0px !important;

    thead {
      tr {
        background-color: var(--table-header-background-color) !important;
        color: var(--Text-Primary) !important;

        th {
          font-family: "GT-america-mono" !important;
          font-size: var(--font-size, 12px) !important;
          font-weight: 400 !important;
          text-transform: uppercase !important;
        }
      }
    }

    th,
    td {
      padding: 5px !important;
      border: 1px solid var(--table-border-color) !important;
    }
  }

  .chat-input {
    display: flex;
    gap: 5px;
    padding: 10px 40px 20px 20px;
    align-items: start;
  }

  .input-wrapper {
    flex: 1;
    display: flex;
    flex-direction: column;
  }

  .chat-input textarea {
    padding: 10px;
    border: 1px solid #ccc;
    border-radius: 8px;
    outline: none;
    color: #111;
    background: #fff;
    height: 70px;
    width: 100%;
    resize: none;
    font-size: 14px;
  }

  .char-counter {
    text-align: right;
    font-size: 12px;
    margin-top: 4px;
    color: #888;
    font-family: monospace;
  }

  .char-counter.warning {
    color: #d97706;
  }

  .char-counter.limit {
    color: #dc2626;
    font-weight: 600;
  }

  .dark & .chat-input textarea {
    background: #2a2a2a !important;
    color: #ffffff !important;
    border: 1px solid #444 !important;
  }

  .dark .char-counter {
    color: #aaa;
  }

  .dark & .chat-input textarea::placeholder {
    color: #aaa !important;
  }

  .chat-input textarea::placeholder {
    color: #888;
  }

  .typing {
    position: sticky;
    bottom: -5px;
    display: inline-flex;
    align-items: center;
    gap: 10px;
    margin-top: 10px;
    padding: 6px 10px;
    border-radius: 999px;
    user-select: none;
    background-color: var(--signal-grey-light);
    animation: typingFadeIn 0.25s ease;
  }

  .dots {
    display: inline-flex;
    gap: 6px;
  }

  .dot {
    width: 9px;
    height: 9px;
    background-color: var(--title-color);
    border-radius: 50%;
    animation: dotPop 1s infinite ease-in-out;
  }

  .dot:nth-child(2) {
    animation-delay: 0.17s;
  }

  .dot:nth-child(3) {
    animation-delay: 0.32s;
  }

  .wait-message {
    font-size: 13px;
    color: var(--title-color);
    white-space: nowrap;
    animation: messageFade 0.35s ease;
  }

  @keyframes messageFade {
    from {
      opacity: 0;
      transform: translateY(2px);
    }
    to {
      opacity: 1;
      transform: translateY(0);
    }
  }

  @keyframes typingFadeIn {
    from {
      opacity: 0;
      transform: translateY(3px);
    }
    to {
      opacity: 1;
      transform: translateY(0);
    }
  }

  @keyframes dotPop {
    0% {
      transform: translateY(0) scale(1);
      opacity: 0.5;
    }
    20% {
      transform: translateY(-5px) scale(1.2);
      opacity: 1;
    }
    40% {
      transform: translateY(0) scale(1);
      opacity: 0.6;
    }
    100% {
      transform: translateY(0) scale(1);
      opacity: 0.5;
    }
  }

  .dark & {
    background: #1e1e1e;
  }

  .dark .chat-messages {
    background: #1e1e1e;
    color: #e6e6e6;
  }

  .dark .bot {
    color: #e6e6e6;
  }

  .dark .message button.command-button {
    border-color: #4da3ff;
    color: #4da3ff;
    &:hover {
      background: #4da3ff;
      color: #1e1e1e;
    }
  }

  .dark .user {
    color: #4da3ff;
  }

  .dark .chat-input textarea {
    background: #2a2a2a;
    color: #ffffff;
    border: 1px solid #444;
  }

  .dark .chat-input textarea::placeholder {
    color: #aaa;
  }

  .dark .dot {
    background: #bbb;
  }

  @keyframes dotPop {
    0% {
      transform: translateY(0) scale(1);
      opacity: 0.6;
    }
    20% {
      transform: translateY(-6px) scale(1.18);
      opacity: 1;
    }
    40% {
      transform: translateY(0) scale(1);
      opacity: 0.7;
    }
    100% {
      transform: translateY(0) scale(1);
      opacity: 0.6;
    }
  }

  pre {
    background-color: transparent;
  }
`;

const FooterResize = styled.div`
  position: absolute;
  bottom: 0px;
  right: 4px;
  cursor: nwse-resize;
`;

const Title = styled.label`
  font-family: "AllianceNo2-SemiBold" !important;
  color: var(--title-color) !important;
  font-size: 18px;
  font-style: normal;
  font-weight: 500;
  line-height: 18px;
  margin-bottom: 0;
  cursor: grab;

  .dark & {
    color: #ffffff !important;
  }
`;

const StatusBar = styled.div`
  display: flex;
  justify-content: space-between;
  margin-top: 5px;
`;

const StatusBarLeftSide = styled.div`
  display: flex;
  gap: 10px;
`;

const DEFAULT_WAIT_MESSAGES = [
  "Please wait...",
  "Accessing official documentation, please wait...",
  "Analyzing your request, please wait...",
  "Thinking more, please wait...",
  "Elaborating your response, please wait...",
];

const ALARMS_PROMPT = "Please check the system alarms";
const NO_CLIENT_MESSAGE =
  "The AI Assistant is not available: the chatbot is not configured.";
const loadingLabel = "<p><label class='loading'>Thinking...</label></p>";
const BUTTON_CLOSING_TAG = "</button>";

const INTERACTION = {
  PREFILL_PROMPT_ALARMS: "PREFILL_PROMPT_ALARMS",
  USER_INPUT: "USER_INPUT",
  COMMAND_BUTTON: "COMMAND_BUTTON",
  CLI_REQUEST: "CLI_REQUEST",
};

const cliCommandsRegex = /\[commands:([^\]]*)\]/;
const cliMessagesRegex = /\[messages:([^\]]*)\]/;
const cliMetadataRegex = /\[(?:commands|messages):[^\]]*\]/g;
const emptyParagraphRegex = /<p>\s*<\/p>/g;
const CLI_REQUEST_ROUNDS_MAX = 3;
const CLI_REQUEST_COMMANDS_MAX = 10;
const TROUBLESHOOTING_URLS = ["https://www.bequant.com/docs/troubleshooting"];

// Notes appended to the context when the assistant must conclude with the
// data already retrieved instead of requesting more CLI commands.
const CLI_REQUEST_FAILED_CONTEXT =
  "\n- The execution of the requested CLI commands FAILED and the DATA " +
  "section contains the error instead of their output. DO NOT request more " +
  "CLI commands. Conclude with the available data and tell the user that " +
  "the commands could not be executed and to try again later.\n";
const CLI_REQUEST_REJECTED_CONTEXT =
  "\n- The requested CLI commands ARE NOT available in the CLI_COMMANDS " +
  "section and WERE NOT executed. DO NOT request them again. Conclude with " +
  "the available data.\n";
const CLI_REQUEST_LIMIT_CONTEXT =
  "\n- The limit of CLI data retrieval for this question has been reached. " +
  "DO NOT request more CLI commands. Conclude with the available data and, " +
  "if more data is needed, tell the user to ask a more specific question.\n";

function splitCommaList(value) {
  return value
    .split(",")
    .map((item) => item.trim())
    .filter((item) => item.length > 0);
}

function escapeRegExp(value) {
  return value.replace(/[.*+?^${}()|[\]\\]/g, "\\$&");
}

function commandFailedMessage(err) {
  return (
    "<p>Execution failed, please try it later. More details: " +
    `<strong>${err}</strong></p>`
  );
}

function getDimensions() {
  const ww = window.innerWidth;
  const wh = window.innerHeight;
  const wideScreen = ww > 1170;
  const menuWidth = wideScreen ? 300 : 0;
  const width = wideScreen ? ww / 4 - 15 : Math.min(ww * 0.8, 1600);
  const height = wideScreen ? wh : Math.min(wh * 0.9, 2000);

  return {
    width,
    height,
    top: wideScreen ? 0 : wh / 2 - height / 2,
    left: wideScreen ? (ww / 4) * 3 : (ww - menuWidth) / 2 - width / 2 - 5,
    wideScreen,
  };
}

export default function Chat({ onSide }) {
  const [input, setInput] = useState("");
  const [messages, setMessages] = useState([
    { role: "bot", text: "Hello! How can I help you?" },
  ]);
  const [writing, setWriting] = useState(false);
  const [working, setWorking] = useState(false);
  const messagesEndRef = useRef(null);

  const inputLength = input.length;
  const limitReached = inputLength > MAX_PROMPT_LENGTH;
  const nearLimit = inputLength > MAX_PROMPT_LENGTH * 0.85;

  const { width, height, top, left, wideScreen } = getDimensions();

  const [boxPos, setBoxPos] = useState({
    top,
    left,
  });
  const [boxSize, setBoxSize] = useState({
    width,
    height,
  });
  const [dragging, setDragging] = useState(false);
  const [resizing, setResizing] = useState(false);
  const [farAway, setFarAway] = useState(true);
  const [waitMessageIndex, setWaitMessageIndex] = useState(0);
  const [floating, setFloating] = useState(false);
  const [mode, setMode] = useState("flash");
  const [checkingAlarms, setCheckingAlarms] = useState(false);
  const [showRetryButton, setShowRetryButton] = useState(false);
  const [cliRequest, setCliRequest] = useState(null);

  const busy = writing || checkingAlarms || working;

  const message = useRef("");
  const dragStart = useRef({ x: 0, y: 0, top: 0, left: 0 });
  const resizeStart = useRef({ x: 0, y: 0, width: 0, height: 0 });
  const chatMessagesRef = useRef();

  const lastInteraction = useRef(null);
  const lastInteractionData = useRef({});
  const actionHandled = useRef(null);
  const waitMessages = useRef(DEFAULT_WAIT_MESSAGES);

  const profile = useContext(ProfileContext);
  const { bqnId, bqns, clusterStatus } = useContext(BqnContext);
  const { open, setOpen, action } = useContext(GeminiContext);

  const geminiClient = useGeminiClient();
  const navigate = useNavigate();

  const onMouseMove = useCallback(
    (e) => {
      if (dragging) {
        const deltaX = e.clientX - dragStart.current.x;
        const deltaY = e.clientY - dragStart.current.y;
        setBoxPos({
          top: Math.min(
            Math.max(0, dragStart.current.top + deltaY),
            window.innerHeight - 45,
          ),
          left: Math.min(
            Math.max(0, dragStart.current.left + deltaX),
            window.innerWidth - 45,
          ),
        });
      }
      if (resizing) {
        const deltaX = e.clientX - resizeStart.current.x;
        const deltaY = e.clientY - resizeStart.current.y;
        setBoxSize({
          width: Math.max(200, resizeStart.current.width + deltaX),
          height: Math.max(200, resizeStart.current.height + deltaY),
        });
      }
    },
    [dragging, resizing],
  );

  const onMouseUp = useCallback(() => {
    setDragging(false);
    setResizing(false);
  }, []);

  const resetDimensions = useCallback(() => {
    const { width, height, top, left, wideScreen } = getDimensions();

    setBoxPos({
      top,
      left,
    });
    setBoxSize({
      width,
      height,
    });
    setFloating(!wideScreen);
  }, [getDimensions]);

  useEffect(() => {
    window.addEventListener("mousemove", onMouseMove);
    window.addEventListener("mouseup", onMouseUp);
    return () => {
      window.removeEventListener("mousemove", onMouseMove);
      window.removeEventListener("mouseup", onMouseUp);
    };
  }, [onMouseMove, onMouseUp]);

  useEffect(() => {
    window.addEventListener("resize", resetDimensions);
    return () => {
      window.removeEventListener("resize", resetDimensions);
    };
  }, [resetDimensions]);

  useEffect(() => {
    message.current = "";
  }, [messages]);

  const onMessageReceived = useCallback(
    (message) => {
      const text = String(message);

      setMessages((prev) => [...prev, { role: "bot", text }]);
      setWriting(false);

      const commandsMatch = text.match(cliCommandsRegex);
      if (!commandsMatch) {
        return;
      }

      const commands = splitCommaList(commandsMatch[1]);
      if (commands.length === 0) {
        return;
      }

      const rounds = (lastInteractionData.current.cliRequestRounds ?? 0) + 1;
      if (rounds > CLI_REQUEST_ROUNDS_MAX) {
        console.warn("CLI request limit reached, ignoring further requests.");
        if (lastInteractionData.current.cliRequestConcluded) {
          return;
        }
        lastInteractionData.current.cliRequestConcluded = true;
        setCliRequest({ context: CLI_REQUEST_LIMIT_CONTEXT });
        return;
      }

      const messagesMatch = text.match(cliMessagesRegex);
      const messages = messagesMatch ? splitCommaList(messagesMatch[1]) : [];

      lastInteractionData.current.cliRequestRounds = rounds;
      setCliRequest({
        commands,
        messages: messages.length > 0 ? messages : DEFAULT_WAIT_MESSAGES,
      });
    },
    [setMessages, setWriting, setCliRequest],
  );

  useEffect(() => {
    if (!geminiClient) return;

    if (open) {
      setTimeout(() => scrollToBottom(), 250);
    }
  }, [open, geminiClient]);

  useEffect(() => {
    if (open) {
      resetDimensions();
      setFarAway(false);
    } else {
      setTimeout(() => {
        setFarAway(true);
      }, 300);
    }
  }, [open]);

  useEffect(() => {
    if (writing) {
      scrollToBottom();
    }
  }, [writing]);

  useEffect(() => {
    if (!writing) {
      setWaitMessageIndex(0);
      return;
    }

    const nextIndex = waitMessageIndex + 1;
    if (nextIndex < waitMessages.current.length) {
      const timeoutId = setTimeout(() => {
        setWaitMessageIndex(nextIndex);
      }, 5000);
      return () => clearTimeout(timeoutId);
    }
  }, [writing, waitMessageIndex]);

  useEffect(() => {
    onSide(open && !floating && wideScreen);
  }, [floating, open, onSide, wideScreen]);

  useEffect(() => {
    if (dragging || resizing) {
      onSide(false);
      setFloating(true);
    }
  }, [dragging, resizing, onSide]);

  const sendMessage = useCallback(
    (message, silent = false) => {
      const question = message.question;

      if (!geminiClient) {
        return Promise.reject(NO_CLIENT_MESSAGE);
      }

      if (!silent) {
        const userMessage = { role: "user", text: question };
        setMessages((prev) => [...prev, userMessage]);
        setInput("");
        setWriting(true);
      }

      return geminiClient
        .sendMessage({
          question,
          history: messages,
          mode,
          data: message.data,
          context: getActiveContext() + (message.context ?? ""),
          urls: message.urls,
        })
        .catch((error) => {
          setShowRetryButton(true);
          throw error;
        });
    },
    [geminiClient, messages, mode, setShowRetryButton],
  );

  const handleNavigation = useCallback(
    (anchor) => {
      try {
        const url = new URL(anchor.href, window.location.origin);
        if (url.hostname === window.location.hostname) {
          navigate(url.pathname + url.search + url.hash);
        } else {
          window.open(anchor.href, "_blank", "noopener,noreferrer");
        }
      } catch (err) {
        console.error("Invalid URL in link click:", err);
      }
    },
    [navigate],
  );

  const insertLoadingLabel = useCallback((id) => {
    if (!id) return;

    const idAttribute = new RegExp(`id=["']?${escapeRegExp(id)}(?=["'\\s>])`);

    setMessages((prev) => {
      const index = prev.findIndex((msg) => idAttribute.test(msg.text));
      if (index === -1) return prev;

      const text = prev[index].text;
      const closingTagIndex = text.indexOf(
        BUTTON_CLOSING_TAG,
        text.search(idAttribute),
      );
      if (closingTagIndex === -1) return prev;

      const endOfButton = closingTagIndex + BUTTON_CLOSING_TAG.length;
      const updatedText =
        text.substring(0, endOfButton) +
        loadingLabel +
        text.substring(endOfButton);

      return prev.map((msg, i) =>
        i === index ? { ...msg, text: updatedText } : msg,
      );
    });
  }, []);

  const replaceLoadingLabelWithResponse = useCallback((response) => {
    setMessages((prev) => {
      const index = prev.findIndex((msg) => msg.text.includes(loadingLabel));
      if (index === -1) {
        return [...prev, { role: "bot", text: response }];
      }

      return prev.map((msg, i) =>
        i === index
          ? { ...msg, text: msg.text.replace(loadingLabel, response) }
          : msg,
      );
    });
  }, []);

  const handleCommand = useCallback(
    (button) => {
      if (working) {
        return;
      }

      const command = button.name;
      const id = button.id;

      const isAllowed = CLI_COMMANDS.some((cmd) =>
        new RegExp(cmd.regexp).test(command),
      );
      if (!isAllowed) {
        return window.showModalInfo(
          "Not available",
          `The command "${command}" is not available at this moment. Please try a different option or type a question.`,
        );
      }

      setWorking(true);
      lastInteraction.current = INTERACTION.COMMAND_BUTTON;
      lastInteractionData.current = { question: button };

      insertLoadingLabel(id);

      ifCl
        .run(command)
        .then(
          (response) =>
            sendMessage(
              {
                question: button.innerText,
                data: [
                  {
                    title: command,
                    content: response,
                    cliCommand: command,
                  },
                ],
              },
              true,
            ),
          (err) => {
            setShowRetryButton(true);
            return Promise.reject(commandFailedMessage(err));
          },
        )
        .then(replaceLoadingLabelWithResponse, replaceLoadingLabelWithResponse)
        .finally(() => setWorking(false));
    },
    [sendMessage, insertLoadingLabel, replaceLoadingLabelWithResponse, working],
  );

  const handleClick = useCallback(
    (e) => {
      const anchor = e.target.closest("a");
      const commandButton = e.target.closest("button.command-button");

      if (anchor) {
        e.preventDefault();
        handleNavigation(anchor);
      } else if (commandButton) {
        handleCommand(commandButton);
      }
    },
    [handleNavigation, handleCommand],
  );

  const handleCliConclusion = useCallback(
    (context) => {
      setWriting(true);
      waitMessages.current = DEFAULT_WAIT_MESSAGES;

      return sendMessage(
        {
          question: lastInteractionData.current.question,
          data: lastInteractionData.current.data,
          urls: TROUBLESHOOTING_URLS,
          context,
        },
        true,
      ).then(onMessageReceived, onMessageReceived);
    },
    [sendMessage, onMessageReceived],
  );

  const handleCliResponse = useCallback(
    (response, allowedCommands, failed = false) => {
      const outputs = String(response).split(batchExModeSeparator);
      const isComplete = outputs.length === allowedCommands.length;
      const data = allowedCommands.map((command, index) => ({
        title: command,
        content: isComplete ? outputs[index] : String(response),
        cliCommand: command,
      }));

      const moreData = [...(lastInteractionData.current.data ?? []), ...data];

      return sendMessage(
        {
          question: lastInteractionData.current.question,
          data: moreData,
          urls: TROUBLESHOOTING_URLS,
          context: failed ? CLI_REQUEST_FAILED_CONTEXT : undefined,
        },
        true,
      ).then((message) => {
        lastInteractionData.current.data = moreData;
        return message;
      });
    },
    [sendMessage],
  );

  const handleCliRequest = useCallback(
    (commands, messages) => {
      setWriting(true);

      waitMessages.current = messages;
      lastInteraction.current = INTERACTION.CLI_REQUEST;
      lastInteractionData.current.commands = commands;
      lastInteractionData.current.messages = messages;

      const allowedCommands = commands
        .filter((command) => {
          const isAllowed = CLI_COMMANDS.some((cmd) =>
            new RegExp(cmd.regexp).test(command),
          );
          if (!isAllowed) {
            console.error(`Command "${command}" is not allowed.`);
          }

          return isAllowed;
        })
        .slice(0, CLI_REQUEST_COMMANDS_MAX);

      if (allowedCommands.length === 0) {
        handleCliConclusion(CLI_REQUEST_REJECTED_CONTEXT);
        return;
      }

      ifCl
        .run(allowedCommands.join("\n") + "\n", "batch_exmode")
        .then(
          (response) => handleCliResponse(response, allowedCommands),
          (err) => handleCliResponse(err, allowedCommands, true),
        )
        .then(
          (message) => {
            waitMessages.current = DEFAULT_WAIT_MESSAGES;
            onMessageReceived(message);
          },
          (err) => {
            waitMessages.current = DEFAULT_WAIT_MESSAGES;
            onMessageReceived(err);
          },
        );
    },
    [onMessageReceived, handleCliResponse, handleCliConclusion],
  );

  useEffect(() => {
    if (!open || !chatMessagesRef.current) return;

    chatMessagesRef.current.removeEventListener("click", handleClick, true);
    chatMessagesRef.current.addEventListener("click", handleClick, true);

    return () => {
      if (chatMessagesRef.current) {
        chatMessagesRef.current.removeEventListener("click", handleClick, true);
      }
    };
  }, [chatMessagesRef.current, open, handleClick]);

  const sendUserInput = () => {
    const question = input.trim();

    if (!question || limitReached || busy) return;

    lastInteraction.current = INTERACTION.USER_INPUT;
    lastInteractionData.current = { question };
    sendMessage({ question }).then(onMessageReceived, onMessageReceived);
  };

  const handleKeyDown = (e) => {
    if (e.key === "Enter" && !e.shiftKey) {
      e.preventDefault();
      sendUserInput();
    }
  };

  const handleInputChange = (e) => {
    setInput(e.target.value.slice(0, MAX_PROMPT_LENGTH));
  };

  const scrollToBottom = () => {
    messagesEndRef.current?.scrollIntoView({ block: "end", inline: "nearest" });
  };

  const onMouseDown = (e) => {
    setDragging(true);
    dragStart.current = {
      x: e.clientX,
      y: e.clientY,
      top: boxPos.top,
      left: boxPos.left,
    };
    e.preventDefault();
  };

  const onResizeMouseDown = (e) => {
    setResizing(true);
    resizeStart.current = {
      x: e.clientX,
      y: e.clientY,
      width: boxSize.width,
      height: boxSize.height,
    };
    e.stopPropagation();
  };

  const onClose = () => {
    setMessages([{ role: "bot", text: "Hello again! How can I help you?" }]);
    setOpen(false);
  };

  const processAlarmsResponse = useCallback(
    (input) => {
      const isCluster = clusterStatus?.mode === "cluster";
      const lines = input.trim().split("\n");
      const [header, ...rows] = lines;
      const headerIndex = getIndexes(header);
      const hostnames = (bqns ?? []).reduce((acc, bqn) => {
        acc[bqn.bqnId] = bqn.hostname;
        return acc;
      }, {});

      let output = `${isCluster ? "BQN " : ""}TYPE  STATUS  TIME\n`;

      for (const row of rows) {
        const attrs = row.trim().split(/\s+/);
        const status = attrs[headerIndex["STATUS"]];
        const bqn = attrs[headerIndex["BQN"]];
        const type = attrs[headerIndex["TYPE"]];
        const time = attrs[headerIndex["TIME"]];

        if (
          ["unknown", "normal"].includes(status) ||
          (isCluster && bqnId !== "all" && Number(bqn) !== Number(bqnId))
        ) {
          continue;
        }

        const bqnName = hostnames[bqn] ? `${bqn} - ${hostnames[bqn]}` : bqn;

        output += `${
          isCluster ? bqnName + " " : ""
        }${type}  ${status}  ${time}\n`;
      }

      return output;
    },
    [clusterStatus, bqns, bqnId],
  );

  const checkAlarms = useCallback(
    (prompt = null) => {
      if (busy || !geminiClient) return;

      setCheckingAlarms(true);
      lastInteraction.current = INTERACTION.PREFILL_PROMPT_ALARMS;
      lastInteractionData.current = { question: prompt ?? ALARMS_PROMPT };

      ifCl
        .run("show system alarms")
        .then(
          (responseAlarms) => {
            const data = [
              {
                title: "System alarms",
                content: processAlarmsResponse(responseAlarms),
                cliCommand: "show system alarms",
              },
            ];

            lastInteractionData.current.data = data;

            return sendMessage({
              question: prompt ?? ALARMS_PROMPT,
              data,
              urls: TROUBLESHOOTING_URLS,
            });
          },
          (err) => {
            setShowRetryButton(true);
            return Promise.reject(commandFailedMessage(err));
          },
        )
        .then(onMessageReceived, onMessageReceived)
        .finally(() => setCheckingAlarms(false));
    },
    [busy, geminiClient, sendMessage, processAlarmsResponse, onMessageReceived],
  );

  // Each action object is handled once, even if the dependencies of the
  // effect change while the action is being processed.
  useEffect(() => {
    if (!action || actionHandled.current === action) {
      return;
    }
    actionHandled.current = action;

    if (action.action === CHATBOT_ACTION.CHECK_ALARMS) {
      if (!open) {
        setOpen(true);
      }
      if (busy) {
        setMessages((prev) => [
          ...prev,
          {
            role: "bot",
            text:
              "I am still working on the previous request. Please wait " +
              "until it finishes and try again.",
          },
        ]);
        return;
      }
      checkAlarms(action.prompt);
    }
  }, [action, open, busy, setOpen, checkAlarms]);

  useEffect(() => {
    if (!cliRequest) {
      return;
    }

    setCliRequest(null);
    if (cliRequest.commands) {
      handleCliRequest(cliRequest.commands, cliRequest.messages);
    } else {
      handleCliConclusion(cliRequest.context);
    }
  }, [cliRequest, handleCliRequest, handleCliConclusion]);

  const retry = useCallback(() => {
    setShowRetryButton(false);
    if (lastInteraction.current === null) {
      return;
    }

    if (lastInteraction.current === INTERACTION.PREFILL_PROMPT_ALARMS) {
      checkAlarms(lastInteractionData.current.question);
    } else if (lastInteraction.current === INTERACTION.USER_INPUT) {
      sendMessage({ question: lastInteractionData.current.question }).then(
        onMessageReceived,
        onMessageReceived,
      );
    } else if (lastInteraction.current === INTERACTION.COMMAND_BUTTON) {
      handleCommand(lastInteractionData.current.question);
    } else if (lastInteraction.current === INTERACTION.CLI_REQUEST) {
      handleCliRequest(
        lastInteractionData.current.commands,
        lastInteractionData.current.messages,
      );
    }
  }, [
    checkAlarms,
    sendMessage,
    handleCommand,
    onMessageReceived,
    handleCliRequest,
  ]);

  const cleanMetadata = (text) => {
    return text.replace(cliMetadataRegex, "").replace(emptyParagraphRegex, "");
  };

  if (!profile) return null;

  const isDark = login.isTheme("dark");

  return (
    <Box
      top={boxPos.top}
      left={farAway ? -3000 : boxPos.left}
      width={boxSize.width}
      height={boxSize.height}
      open={open}
      className={`${isDark ? "dark" : ""}${floating ? " floating" : ""}`}
    >
      <Header onMouseDown={onMouseDown}>
        <div className="left">
          <AutoAwesomeIcon className="material-icons font-size-80" />
          <Title> BQN AI Assistant </Title>
        </div>
        <div className="right">
          {wideScreen && floating && (
            <button
              onClick={resetDimensions}
              title="Quarter Right"
              onMouseDown={(e) => e.stopPropagation()}
            >
              <VerticalAlignTopIcon className="rotate-right" />
            </button>
          )}
          <button
            onClick={() => setOpen(false)}
            onMouseDown={(e) => e.stopPropagation()}
          >
            <MinimizeIcon />
          </button>
          <button onClick={onClose} onMouseDown={(e) => e.stopPropagation()}>
            <CloseIcon />
          </button>
        </div>
      </Header>

      <Content>
        <CheckLicenseFirst reloadSignal={open}>
          <div ref={chatMessagesRef} className="chat-messages">
            {messages.map((m, i) => (
              <div
                key={i}
                className={`message ${m.role}`}
                dangerouslySetInnerHTML={{
                  __html: DOMPurify.sanitize(cleanMetadata(m.text)),
                }}
              ></div>
            ))}
            {writing && (
              <div className="typing">
                <div className="dots">
                  <div className="dot" aria-hidden="true"></div>
                  <div className="dot" aria-hidden="true"></div>
                  <div className="dot" aria-hidden="true"></div>
                </div>
                {waitMessages.current[waitMessageIndex] && (
                  <span key={waitMessageIndex} className="wait-message">
                    {waitMessages.current[waitMessageIndex]}
                  </span>
                )}
              </div>
            )}
            <div ref={messagesEndRef} />
          </div>

          <div className="chat-input">
            <div className="input-wrapper">
              {showRetryButton && (
                <button
                  onClick={retry}
                  type="button"
                  className="btn btn-link waves-effect"
                >
                  Retry
                </button>
              )}
              <textarea
                type="text"
                value={input}
                disabled={busy}
                onChange={handleInputChange}
                onKeyDown={handleKeyDown}
                placeholder="Type your message..."
                maxLength={MAX_PROMPT_LENGTH}
              />

              <StatusBar>
                <StatusBarLeftSide>
                  <SelectInput
                    options={[
                      { label: "Normal", value: "flash" },
                      { label: "Fast", value: "lite" },
                      { label: "Quality", value: "pro" },
                    ]}
                    defaultValue={mode}
                    onChange={(event) => setMode(event.target.value)}
                  />
                </StatusBarLeftSide>

                <div
                  className={`char-counter ${
                    limitReached ? "limit" : nearLimit ? "warning" : ""
                  }`}
                >
                  {inputLength} / {MAX_PROMPT_LENGTH}
                </div>
              </StatusBar>
            </div>

            <button
              onClick={sendUserInput}
              disabled={!input.trim() || limitReached || busy}
              type="button"
              className="btn btn-link waves-effect"
            >
              <SendIcon />
            </button>
          </div>
        </CheckLicenseFirst>
      </Content>

      <FooterResize onMouseDown={onResizeMouseDown}>
        <DragIndicatorIcon />
      </FooterResize>
    </Box>
  );
}
